import CampaignRemoteSource from './schema/CampaignRemoteSource';
import Journey from './Journey';
import LogDataType from 'telemetry/lib/schema/LogDataType';
import React from 'react';
import { CAMPAIGN_RENDERED, CAMPAIGN_STARTED } from '../utils/eventNames';
import { CampaignConfig } from './schema/CampaignConfig';
import { CampaignEndSignal } from './schema/CampaignEndSignal';
import { CampaignRenderProps } from './schema/CampaignRenderProps';
import { CampaignStatus } from './schema/CampaignStatus';
import { CampaignType } from './schema/CampaignType';
import { CampaignUnit } from '../components/CampaignUnit';
import { getDataLogger } from 'telemetry';
import { getOpxHostAppName } from 'mini-opx-config/lib/getOpxHostAppName';
import { onSignal } from 'listeners/lib/signalTrackerListener';

export interface CampaignRendererProps<T> {
    /* View type props to be passed when embedding campaign unit */
    campaignViewTypeProps: T;

    /* Journey function if needed */
    journey?: Journey;
}

/**
 * Class to instantiate a campaign object for referencing throughout the flow of the campaign experience.
 * This will be instantiated by developers and passed on to the CampaignUnit as props
 */
class Campaign<T> {
    private remoteCampaignId: string;
    public renderProps: CampaignRenderProps;
    private campaignSource: CampaignRemoteSource;
    public promise: Promise<CampaignStatus>;
    public promiseResolve: (value: CampaignStatus | PromiseLike<CampaignStatus>) => void;
    private campaignType: CampaignType<T>;
    private rule: () => Promise<boolean>;
    public logger: LogDataType;
    public logState: { [key: string]: boolean } = {
        CAMPAIGN_STARTED: false,
        CAMPAIGN_RENDERED: false,
        CAMPAIGN_ENDED: false,
    };

    constructor(campaignConfig: CampaignConfig) {
        this.remoteCampaignId = campaignConfig.remoteCampaignId
            ? campaignConfig.remoteCampaignId
            : null;
        this.campaignSource = campaignConfig.campaignSource;
        this.campaignType = campaignConfig.campaignType;
        this.rule = campaignConfig.rule;
        this.logger = getDataLogger();
        this.renderProps = campaignConfig.renderProps;
    }

    public getRemoteCampaignId(): string {
        return this.remoteCampaignId;
    }

    public getRemoteSource(): CampaignRemoteSource {
        return this.campaignSource;
    }

    public getRule(): () => Promise<boolean> {
        return this.rule;
    }

    public getRenderProps(): CampaignRenderProps {
        return this.renderProps;
    }

    public async start(): Promise<CampaignStatus> {
        /* tslint:disable:promise-must-complete */
        const self = this;
        this.promise = new Promise(resolve => (self.promiseResolve = resolve));
        /* tslint:enable:promise-must-complete */
        let ruleResult = false;
        if (this.rule) {
            ruleResult = await this.rule();
        }

        if (ruleResult) {
            // Await for the user/custom event signals to happen and then start the campaign
            if (this.renderProps?.eventSignal) {
                await Promise.all([...this.renderProps.eventSignal.split(',').map(onSignal)]);
            }
            if (this.remoteCampaignId) {
                if (!this.logState[CAMPAIGN_STARTED]) {
                    this.logState[CAMPAIGN_STARTED] = true;
                    this.logger?.(
                        CAMPAIGN_STARTED,
                        { id: this.remoteCampaignId, hostApp: getOpxHostAppName() },
                        { isCore: true }
                    );
                }
                this.campaignType.start(this);
            } else {
                this.promiseResolve(null);
            }
        } else {
            this.promiseResolve(null);
        }
        return this.promise;
    }

    public ready(): void {
        this.campaignType.ready?.(this);
    }

    public end(endSignal: CampaignEndSignal) {
        this.promise && this.campaignType.end(this, endSignal);
    }

    public render(viewProps: T): JSX.Element {
        if (!this.logState[CAMPAIGN_RENDERED]) {
            this.logState[CAMPAIGN_RENDERED] = true;
            this.logger?.(
                CAMPAIGN_RENDERED,
                { id: this.remoteCampaignId, hostApp: getOpxHostAppName() },
                { isCore: true }
            );
        }
        return this.campaignType.render(this, viewProps);
    }

    public unit = (props: CampaignRendererProps<T>) => (
        <CampaignUnit
            journey={props.journey}
            campaign={this}
            viewProps={props.campaignViewTypeProps}
        />
    );
}

export default Campaign;
